import React, { useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import LoadingSpinner from '../common/LoadingSpinner';
import type { StatsGranularity } from '../../services/types';
import type { StatSeries } from './statSeries';
import TimeSeriesChart, { CHART_HEIGHT } from './TimeSeriesChart';
import TimeSeriesTable from './TimeSeriesTable';

/** Props der StatChartCard-Komponente. */
interface StatChartCardProps {
  /** ID-Praefix der Card und aller Kind-Elemente (z. B. "chart-contracts"). */
  readonly chartId: string;
  /** Deutscher Card-Titel (benennt bei einer Serie auch die Serie). */
  readonly title: string;
  /** Darstellungsart des Charts. */
  readonly variant: 'bar' | 'line';
  /** Granularitaet (Perioden-Formatierung in Chart und Tabelle). */
  readonly granularity: StatsGranularity;
  /** Eine oder zwei Serien; leer, solange keine Daten vorliegen. */
  readonly series: readonly StatSeries[];
  /** true, waehrend die Zeitreihe(n) geladen werden. */
  readonly loading: boolean;
  /** Fehlermeldung oder null (rendert Alert mit id `<chartId>-error`). */
  readonly error: string | null;
  /**
   * true, wenn der eingegebene Zeitraum ungueltig ist. Die Card zeigt dann
   * weiterhin den letzten gueltigen Datenstand, markiert ihn aber sichtbar
   * als veraltet (es werden bewusst keine neuen Daten geladen).
   */
  readonly staleRange?: boolean;
}

/**
 * Card-Wrapper eines Dashboard-Charts: Titel, Umschalter Diagramm/Tabelle
 * sowie Lade-, Fehler- und Leer-Zustand. Chart und Tabelle erhalten
 * dieselben Serien; die Hoehe bleibt in allen Zustaenden stabil.
 */
function StatChartCard({
  chartId,
  title,
  variant,
  granularity,
  series,
  loading,
  error,
  staleRange = false,
}: StatChartCardProps): React.JSX.Element {
  const [showTable, setShowTable] = useState<boolean>(false);
  const hasData = series.some((singleSeries) => singleSeries.points.length > 0);

  return (
    <Card id={chartId} className="h-100 shadow-sm">
      <Card.Body id={`${chartId}-body`}>
        <div
          id={`${chartId}-header`}
          className="d-flex justify-content-between align-items-center gap-2 mb-3"
        >
          <Card.Title as="h2" id={`${chartId}-title`} className="h6 mb-0">
            {title}
          </Card.Title>
          <Button
            id={`${chartId}-table-toggle`}
            variant="outline-secondary"
            size="sm"
            disabled={loading || error !== null || !hasData}
            onClick={() => setShowTable((previous) => !previous)}
            title={showTable ? 'Als Diagramm anzeigen' : 'Als Tabelle anzeigen'}
          >
            <i
              id={`${chartId}-table-toggle-icon`}
              className={`bi ${showTable ? 'bi-bar-chart' : 'bi-table'} me-1`}
            ></i>
            {showTable ? 'Diagramm' : 'Tabelle'}
          </Button>
        </div>

        {staleRange && (
          <div id={`${chartId}-stale-hint`} className="small text-warning-emphasis mb-2">
            <i
              id={`${chartId}-stale-hint-icon`}
              className="bi bi-exclamation-triangle me-1"
            ></i>
            Ungültiger Zeitraum – angezeigt wird der zuletzt geladene Stand.
          </div>
        )}

        {loading && (
          <div
            id={`${chartId}-loading-container`}
            className="d-flex align-items-center justify-content-center"
            style={{ height: CHART_HEIGHT }}
          >
            <LoadingSpinner id={`${chartId}-loading`} />
          </div>
        )}

        {/*
          Fehler-Alert in einen Container mit CHART_HEIGHT legen: Sonst waere
          die Card im Fehlerfall deutlich niedriger und das Charts-Grid
          wuerde springen.
        */}
        {!loading && error !== null && (
          <div
            id={`${chartId}-error-container`}
            className="d-flex align-items-center"
            style={{ height: CHART_HEIGHT }}
          >
            <Alert id={`${chartId}-error`} variant="danger" className="mb-0 w-100">
              {error}
            </Alert>
          </div>
        )}

        {!loading && error === null && !hasData && (
          <div
            id={`${chartId}-empty`}
            className="d-flex align-items-center justify-content-center text-muted"
            style={{ height: CHART_HEIGHT }}
          >
            Keine Daten im gewählten Zeitraum.
          </div>
        )}

        {!loading && error === null && hasData && (
          showTable ? (
            <TimeSeriesTable chartId={chartId} granularity={granularity} series={series} />
          ) : (
            <TimeSeriesChart
              chartId={chartId}
              variant={variant}
              granularity={granularity}
              series={series}
            />
          )
        )}
      </Card.Body>
    </Card>
  );
}

export default StatChartCard;
